import {
  NotificationItem,
  isNotificationUnread,
} from "../utils/types";

interface NotifyProps {
  notification: NotificationItem;
  onPress: (notification: NotificationItem) => void;
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const NotificationIcon = ({ category }: { category?: string }) => {
  const name = category?.toLowerCase();
  const stroke = "currentColor";

  if (name === "order") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6z"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 6h18M16 10a4 4 0 01-8 0"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (name === "wallet" || name === "payout") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect
          x="2"
          y="5"
          width="20"
          height="14"
          rx="2"
          stroke={stroke}
          strokeWidth="1.8"
        />
        <path
          d="M16 12h.01"
          stroke={stroke}
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (name === "promotion" || name === "coupon") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 7h.01"
          stroke={stroke}
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.73 21a2 2 0 01-3.46 0"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Notify = ({ notification, onPress }: NotifyProps) => {
  const isUnread = isNotificationUnread(notification);

  return (
    <button
      type="button"
      onClick={() => onPress(notification)}
      className={`w-full text-left rounded-[16px] border flex items-start gap-3 p-4 mb-[12px] transition-colors ${
        isUnread
          ? "bg-[#FF8A00]/10 border-[#FF8A00]/30 dark:bg-[#FF8A00]/15 dark:border-[#FF8A00]/40"
          : "bg-white dark:bg-gray-800 border-[#E9E9E9] dark:border-gray-700"
      }`}
    >
      <div
        className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUnread
            ? "bg-[#FF8A00]/20 text-[#FF8A00]"
            : "bg-[#F5F5F5] dark:bg-gray-700 text-[#6A6A6A] dark:text-gray-400"
        }`}
      >
        <NotificationIcon category={notification.category} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h6
            className={`flex-1 truncate text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] leading-[20px] ${
              isUnread ? "font-semibold" : "font-medium"
            }`}
          >
            {notification.title}
          </h6>
          {isUnread && (
            <span className="h-2 w-2 rounded-full bg-[#FF8A00] flex-shrink-0" />
          )}
        </div>
        <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal leading-[17px] mt-1 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-[#6A6A6A] dark:text-gray-500 font-Urbanist text-[10px] font-medium leading-[17px] mt-1.5">
          {formatDateTime(notification.createdAt)}
        </p>
      </div>
    </button>
  );
};

export default Notify;
